import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { FEE_LEVEL_LINE_MARKUP_FEE_LINE, feeLevelLabel } from '../utils/contractFees';

function formatMoney(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return amount < 0 ? `($${abs})` : `$${abs}`;
}

function formatPct(value) {
  if (value === null || value === undefined) return '—';
  const pct = Number(value);
  if (Math.abs(pct - Math.round(pct)) < 0.05) return `${Math.round(pct)}%`;
  return `${pct.toFixed(1)}%`;
}

const SEVERITY_STYLES = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-gray-100 text-gray-700',
};

const ProjectAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProjectVuid, setSelectedProjectVuid] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/projects`);
        const list = Array.isArray(response.data) ? response.data : [];
        setProjects(list.filter((project) => project.status !== 'cancelled'));
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load projects');
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('project');
    if (fromUrl) {
      setSelectedProjectVuid(fromUrl);
      return;
    }
    if (!selectedProjectVuid && projects.length === 1) {
      setSelectedProjectVuid(projects[0].vuid);
    }
  }, [location.search, projects, selectedProjectVuid]);

  useEffect(() => {
    if (!selectedProjectVuid) {
      setAnalysis(null);
      return;
    }
    const loadAnalysis = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/project-analysis`, {
          params: { project_vuid: selectedProjectVuid },
        });
        const rows = response.data?.projects || [];
        setAnalysis(rows[0] || null);
      } catch (err) {
        setAnalysis(null);
        setError(err.response?.data?.error || err.message || 'Failed to load project analysis');
      } finally {
        setLoading(false);
      }
    };
    loadAnalysis();
  }, [selectedProjectVuid]);

  const handleProjectChange = (projectVuid) => {
    setSelectedProjectVuid(projectVuid);
    if (projectVuid) {
      navigate(`/project-analysis?project=${projectVuid}`);
    } else {
      navigate('/project-analysis');
    }
  };

  const findings = analysis?.findings || [];
  const fee = analysis?.fee;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project Analysis</h1>
          <p className="text-sm text-gray-600 mt-1">
            Flags missing change-order fees, fee-rate drift, over/under billing, and costs that are not on the contract.
          </p>
        </div>
        <div className="w-full md:w-80">
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select
            value={selectedProjectVuid}
            onChange={(event) => handleProjectChange(event.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800"
          >
            <option value="">Select a project...</option>
            {projects.map((project) => (
              <option key={project.vuid} value={project.vuid}>
                {project.project_number} {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {!selectedProjectVuid && !loading && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          Select a project to run analysis.
        </div>
      )}

      {loading && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          Analyzing project…
        </div>
      )}

      {!loading && analysis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total contract</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(analysis.total_contract)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Current budget</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(analysis.current_budget)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Costs / billings</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{formatMoney(analysis.costs_to_date)}</p>
              <p className="text-xs text-gray-500">{formatMoney(analysis.billings_to_date)} billed</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Over / under billing</p>
              <p className={`text-lg font-bold mt-1 ${analysis.over_under_billing > 0 ? 'text-red-700' : analysis.over_under_billing < 0 ? 'text-amber-700' : 'text-gray-900'}`}>
                {formatMoney(analysis.over_under_billing)}
              </p>
              <p className="text-xs text-gray-500">
                {formatPct(analysis.percent_complete)} complete · {formatPct(analysis.percent_billed)} billed
              </p>
            </div>
          </div>

          {fee && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">{feeLevelLabel(fee.fee_level || analysis.fee_level)}</h2>
                <span className="text-sm text-gray-600">
                  {fee.uses_stored_fee_percent
                    ? `Contract fee ${formatPct(fee.original_fee_percent)}`
                    : `Original ${formatPct(fee.original_fee_percent)} → current ${formatPct(fee.current_fee_percent)}`}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {(fee.fee_level || analysis.fee_level) === FEE_LEVEL_LINE_MARKUP_FEE_LINE
                  ? 'Per-line markups are billed on the dedicated fee cost code. Labor billed from a rate schedule is excluded from fee unless the contract line is set to include it. '
                  : ''}
                Fee is {formatMoney(fee.original_fee)} on the original contract and {formatMoney(fee.current_fee)} including approved change orders.
                {(fee.fee_level || analysis.fee_level) === FEE_LEVEL_LINE_MARKUP_FEE_LINE
                  ? ''
                  : fee.uses_stored_fee_percent
                    ? ` Expected change-order fees use the stored ${formatPct(fee.original_fee_percent)} rate.`
                    : ' Rate is fee ÷ (contract − fee line).'}
              </p>
              {(fee.change_orders || []).length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">CO</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Contract change</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Expected fee</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Gap</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {fee.change_orders.map((row) => (
                        <tr key={row.vuid} className={row.fee_gap >= 25 ? 'bg-red-50' : ''}>
                          <td className="px-3 py-2 text-sm font-medium text-gray-900">{row.change_order_number}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{row.description}</td>
                          <td className="px-3 py-2 text-sm text-right">{formatMoney(row.contract_change)}</td>
                          <td className="px-3 py-2 text-sm text-right">{formatMoney(row.fee_amount)}</td>
                          <td className="px-3 py-2 text-sm text-right">{formatMoney(row.expected_fee)}</td>
                          <td className="px-3 py-2 text-sm text-right font-medium">
                            {formatMoney(row.fee_gap)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Findings</h2>
              <div className="text-sm text-gray-600">
                {analysis.finding_counts?.high || 0} high · {analysis.finding_counts?.medium || 0} medium · {analysis.finding_counts?.low || 0} low
              </div>
            </div>
            {findings.length === 0 ? (
              <p className="text-sm text-gray-500">No anomalies flagged for this project.</p>
            ) : (
              <div className="space-y-3">
                {findings.map((finding) => (
                  <div key={finding.id} className="border border-gray-200 rounded-md p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.low}`}>
                            {finding.severity}
                          </span>
                          <span className="text-xs uppercase tracking-wider text-gray-500">{finding.category}</span>
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900">{finding.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{finding.detail}</p>
                      </div>
                      {finding.amount !== undefined && finding.amount !== null && (
                        <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                          {finding.unit === 'percent' ? formatPct(finding.amount) : formatMoney(finding.amount)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Link to={`/projects/${analysis.project_vuid}`} className="btn-claret">
              Project details
            </Link>
            <Link to="/wip" className="btn-claret">
              WIP report
            </Link>
            <Link to="/external-change-orders" className="btn-claret">
              Change orders
            </Link>
            <Link to="/project-billing" className="btn-claret">
              Project billing
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectAnalysis;
